import { useEffect, useState } from 'react';
import { useEscapeToClose, useFocusTrap } from '@/lib/use-escape';

/**
 * 자주 틀리는 이름 등록.
 *
 * 왜 있나: 한국 클립 편집의 최대 병목이 자막이고, 거기서 **실제로 깨지는 건 고유명사**다
 * (닉네임·게임 용어·밈). 이 목록은 전사할 때 whisper 에 미리 알려줘서 **처음부터 그 표기로
 * 받아 적게** 한다 — 나중에 고치는 것보다 정확하고, 무엇보다 자막 타이밍을 안 건드린다.
 * (`docs/shorts-editor-input-redesign.md` D4)
 */

export interface VocabularyApi {
  load: () => Promise<{ terms: string[]; maxTerms: number; maxTermLength: number }>;
  save: (terms: string[]) => Promise<void>;
}

export function VocabularyModal({ api, onClose }: { api: VocabularyApi; onClose: () => void }) {
  useEscapeToClose(onClose);
  const trapRef = useFocusTrap<HTMLDivElement>();
  const [terms, setTerms] = useState<string[] | null>(null);
  const [limits, setLimits] = useState({ maxTerms: 60, maxTermLength: 30 });
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void api
      .load()
      .then((r) => {
        if (!alive) return;
        setTerms(r.terms);
        setLimits({ maxTerms: r.maxTerms, maxTermLength: r.maxTermLength });
      })
      .catch(() => alive && setError('목록을 불러오지 못했어요.'));
    return () => {
      alive = false;
    };
  }, [api]);

  const add = () => {
    const t = draft.trim().slice(0, limits.maxTermLength);
    if (!t || !terms) return;
    // 대소문자·공백만 다른 것은 같은 말이다. 중복은 whisper prompt 예산만 축낸다.
    if (terms.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setDraft('');
      return;
    }
    if (terms.length >= limits.maxTerms) return;
    setTerms([...terms, t]);
    setDraft('');
  };

  const full = (terms?.length ?? 0) >= limits.maxTerms;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      ref={trapRef}
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-[420px] flex-col rounded-lg border bg-card p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-1 text-sm font-semibold">자주 틀리는 이름</h2>
        <p className="mb-3 text-[11px] leading-relaxed text-muted-foreground">
          닉네임·게임 용어·밈처럼 자막에서 자꾸 틀리는 말을 미리 알려주면, 자막을 만들 때부터 그
          표기로 적어요.
        </p>

        {error && <p className="mb-2 text-xs text-destructive">{error}</p>}
        {terms === null ? (
          <p className="py-8 text-center text-xs text-muted-foreground">불러오는 중…</p>
        ) : (
          <>
            <div className="mb-3 flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
              {terms.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTerms(terms.filter((x) => x !== t))}
                  className="rounded-full bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground"
                  title="빼기"
                >
                  {t} ✕
                </button>
              ))}
              {terms.length === 0 && (
                <p className="text-[11px] text-muted-foreground">
                  예를 들어 방송 닉네임, 자주 하는 게임 이름, 시청자들이 쓰는 밈이요.
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <input
                value={draft}
                maxLength={limits.maxTermLength}
                disabled={full}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    add();
                  }
                }}
                placeholder={full ? '' : '용어를 넣고 Enter'}
                className="flex-1 rounded-md border bg-background px-2 py-1.5 text-sm disabled:opacity-40"
              />
              <button
                type="button"
                onClick={add}
                disabled={full || !draft.trim()}
                className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-40"
              >
                추가
              </button>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              {full
                ? `${limits.maxTerms}개가 다 찼어요. 새로 넣으려면 안 쓰는 걸 빼 주세요.`
                : `${terms.length}개 · ${limits.maxTerms}개까지 넣을 수 있어요`}
            </p>
          </>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          {/*
            지금 이 클립 자막은 이미 만들어졌다 — 소급되지 않는다는 걸 숨기지 않는다.
            다만 «안 된다»로 끝내지 않는다. 검수 지적: "내 이름 틀린 거 보고 등록하러
            간 건데 지금 이 자막은 안 고쳐진다. 그럼 왜 지금 열어줬냐."
            → 지금 클립을 고치는 방법(타임라인에서 직접 수정)을 같이 알려준다.
          */}
          <span className="mr-auto text-[11px] leading-relaxed text-muted-foreground">
            다음 분석부터 적용돼요. 지금 이 영상의 자막은
            <br />
            타임라인에서 직접 고칠 수 있어요.
          </span>
          <button type="button" onClick={onClose} className="rounded-md border px-3 py-1.5 text-sm">
            닫기
          </button>
          <button
            type="button"
            disabled={terms === null || saving}
            onClick={() => {
              if (!terms) return;
              setSaving(true);
              void api
                .save(terms)
                .then(onClose)
                .catch(() => setError('저장하지 못했어요. 잠시 후 다시 시도해 주세요.'))
                .finally(() => setSaving(false));
            }}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
